
import React, { useEffect } from 'react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Navigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDashboard } from '@/hooks/useDashboard';
import DashboardLoading from '@/components/dashboard/DashboardLoading';
import MobileDashboard from '@/components/dashboard/MobileDashboard';
import DesktopDashboard from '@/components/dashboard/DesktopDashboard';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const Dashboard = () => {
  const {
    user,
    userStats,
    loadingStats,
    authLoading,
    profileLoading,
    isClientSide,
    isProfileComplete,
    userName,
    statsFetchFailed,
    isOfflineMode
  } = useDashboard();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Log debug information about loading state
  useEffect(() => {
    console.log('Dashboard loading states:', {
      authLoading,
      profileLoading,
      isClientSide,
      loadingStats,
      user: !!user,
      isProfileComplete,
      statsFetchFailed,
      isOfflineMode
    });
  }, [authLoading, profileLoading, isClientSide, user, isProfileComplete, loadingStats, statsFetchFailed, isOfflineMode]);

  // Show offline mode toast
  useEffect(() => {
    if (isOfflineMode) {
      toast({
        title: "Limited Connectivity",
        description: "Some features may not be available",
        variant: "destructive"
      });
    }
  }, [isOfflineMode, toast]);

  // Show loading state when authenticating
  if (authLoading || profileLoading || !isClientSide) {
    return <DashboardLoading />;
  }

  // Redirect to auth page if not logged in
  if (!user) {
    console.log('No user found, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  // Handle error scenarios with opportunity to refresh
  if (!isProfileComplete && user) {
    console.log('Profile not complete or missing, showing profile setup option');
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6">
            <AlertCircle className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-center mb-2">Profile Setup Required</h2>
            <p className="text-center text-muted-foreground mb-6">
              We need some additional information to set up your dashboard.
            </p>
            <Button 
              className="w-full" 
              onClick={() => window.location.href = '/profile'}
            >
              Complete Your Profile
            </Button>
            <Button 
              variant="outline" 
              className="w-full mt-4" 
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      {isMobile ? (
        <MobileDashboard 
          userName={userName}
          loadingStats={loadingStats}
          userStats={userStats}
          statsFetchFailed={statsFetchFailed}
        />
      ) : (
        <DesktopDashboard 
          userName={userName}
          loadingStats={loadingStats}
          userStats={userStats}
          statsFetchFailed={statsFetchFailed}
        />
      )}
      <Footer />
    </div>
  );
};

export default Dashboard;
